/**
 * The central, versioned catalogue of event types (02-architecture.md,
 * "Commands, events, audit"). One registry for the whole system: `tx.emit`
 * refuses a definition that is not in it, and the outbox relay and consumers
 * parse stored envelopes with it.
 *
 * Adding an event (in the module's slice):
 *
 *   // packages/contracts/src/events/orders.ts
 *   export const orderSettledV1 = defineEvent('order.settled', 1, z.object({
 *     orderId: uuidSchema, totalPaise: paiseSchema,
 *   }));
 *
 *   // here
 *   export const EVENT_DEFINITIONS = [orderSettledV1, …] as const;
 *
 * Rules:
 * - Type names are dotted, past tense (`order.settled`, `order.kot_sent`).
 * - A payload change that is not purely additive is a new version; keep the old
 *   definition listed until no unconsumed events of it remain in the outbox.
 * - Payload schemas describe the JSON form (money as `paiseSchema`, a codec);
 *   `tx.emit` encodes, `eventRegistry.parse` decodes.
 *
 * Platform (`dead_letter.retried`), iam, org, pricing, menu, integrations, routing
 * (`outlet.routing_changed`), orders (`order.*`) and kitchen (`kot.*`, `ticket.*`,
 * `print_job.*`, setup), payments (`payment.*`, `settlement.pending_marked`) and
 * accounting (`invoice.issued`, `day.*`), reports (`report.day_rebuilt`), customers (`customer.*`, `khata.*`, `reward.*`), inventory (`stock.*`, `stock_item.saved`, `recipe.set`, `wastage.*`, …), bar (`permit.*`, `liquor_profile.saved`, `breakage.recorded`, `bar_closing.recorded`, `excise_report.generated`), notifications (`notification.*`, `popup.raised`, `ops_alert.raised`), aggregators (`aggregator.*`), delivery (`delivery.*`) and storefront (`table_session.*`,
 * `order.rated`, `customer_login.*`, `saved_cart.*`) events so far; domain
 * modules add theirs as they are built. Tests register their own definitions on a
 * separate registry.
 */
import { ACCOUNTING_EVENTS } from './accounting.js';
import { AGGREGATOR_EVENTS } from './aggregators.js';
import { BAR_EVENTS } from './bar.js';
import {
  createEventRegistry,
  type AnyEventDefinition,
  type EventOf,
  type EventRegistry,
} from './events.js';
import { IAM_EVENTS } from './iam.js';
import { INVENTORY_EVENTS } from './inventory.js';
import { INTEGRATIONS_EVENTS } from './integrations.js';
import { KITCHEN_EVENTS } from './kitchen.js';
import { MENU_EVENTS } from './menu.js';
import { NOTIFICATION_EVENTS } from './notifications.js';
import { ONLINE_EVENTS } from './online.js';
import { ORDER_EVENTS } from './orders.js';
import { ORG_EVENTS } from './org.js';
import { CUSTOMER_EVENTS } from './customers.js';
import { DELIVERY_EVENTS } from './delivery.js';
import { PAYMENT_EVENTS } from './payments.js';
import { deadLetterRetriedV1 } from './platform.js';
import { PRICING_EVENTS } from './pricing.js';
import { REPORT_EVENTS } from './reports.js';
import { ROUTING_EVENTS } from './routing.js';
import { STOREFRONT_EVENTS } from './storefront.js';

export const EVENT_DEFINITIONS = [
  deadLetterRetriedV1,
  ...IAM_EVENTS,
  ...ORG_EVENTS,
  ...PRICING_EVENTS,
  ...MENU_EVENTS,
  ...INTEGRATIONS_EVENTS,
  ...ROUTING_EVENTS,
  ...ORDER_EVENTS,
  ...KITCHEN_EVENTS,
  ...PAYMENT_EVENTS,
  ...ACCOUNTING_EVENTS,
  ...STOREFRONT_EVENTS,
  ...ONLINE_EVENTS,
  ...REPORT_EVENTS,
  ...CUSTOMER_EVENTS,
  ...INVENTORY_EVENTS,
  ...BAR_EVENTS,
  ...NOTIFICATION_EVENTS,
  ...AGGREGATOR_EVENTS,
  ...DELIVERY_EVENTS,
] as const satisfies readonly AnyEventDefinition[];

/** Every event the system emits, as a union of parsed envelopes. */
export type DomainEvent = EventOf<(typeof EVENT_DEFINITIONS)[number]>;

export const eventRegistry: EventRegistry<(typeof EVENT_DEFINITIONS)[number]> = createEventRegistry(
  ...EVENT_DEFINITIONS,
);
